import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import React from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  TenantSessionProvider,
  useTenantSession,
} from "@/contexts/TenantSessionContext";
import { SuperAdminProvider, useSuperAdmin } from "@/contexts/SuperAdminContext";
import { ProductTour } from "@/components/tour/ProductTour";
import SuperAdminLogin from "./pages/superadmin/SuperAdminLogin";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import AccountsList from "./pages/superadmin/AccountsList";
import AIUsage from "./pages/superadmin/AIUsage";
import AccountAuditLog from "./pages/superadmin/AccountAuditLog";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import LeaveManagement from "./pages/LeaveManagement";
import Timekeeping from "./pages/Timekeeping";
import TimekeepingAdmin from "./pages/TimekeepingAdmin";
import TeamLeave from "./pages/TeamLeave";
import Performance from "./pages/Performance";
import Recruitment from "./pages/Recruitment";
import Apply from "./pages/Apply";
import InterviewSchedule from "./pages/InterviewSchedule";
import PreBoarding from "./pages/PreBoarding";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MyProfile from "./pages/MyProfile";
import EmployeeProfile from "./pages/EmployeeProfile";
import Payroll from "./pages/Payroll";
import ModuleDisabled from "./pages/ModuleDisabled";
import Organization from "./pages/Organization";
import MySalary from "./pages/MySalary";
import TeamOverview from "./pages/TeamOverview";
import Onboarding from "./pages/Onboarding";
import Offboarding from "./pages/Offboarding";
import MyOnboarding from "./pages/MyOnboarding";
import OnboardingMessages from "./pages/OnboardingMessages";
import Surveys from "./pages/Surveys";
import MySurveys from "./pages/MySurveys";
import Workflows from "./pages/Workflows";
import MyTasks from "./pages/MyTasks";
import AuditLog from "./pages/AuditLog";
import GdprRetention from "./pages/GdprRetention";
import Settings from "./pages/Settings";
import {
  CareerDevelopment,
  Compliance,
  // Line Manager
  TeamPerformance,
  Approvals,
  // Career Coach
  Coaching,
  CareerPlans,
  Mentoring,
  // C-Level
  Analytics,
  Reports,
  StrategicPlanning,
} from "./pages/PlaceholderPages";
import { Documents } from "./pages/Documents";
import MyDocuments from "./pages/MyDocuments";
import CorporateCalendar from "./pages/CorporateCalendar";
import CorporateCalendarEvent from "./pages/CorporateCalendarEvent";
import CorporateEventsAdmin from "./pages/CorporateEventsAdmin";
import FieldPermissionsSettings from "./pages/FieldPermissionsSettings";
import NavPermissionsSettings from "./pages/NavPermissionsSettings";
import TeamsManagement from "./pages/TeamsManagement";
import Notifications from "./pages/Notifications";
import NotFound from "./pages/NotFound";
import HRAnalytics from "./pages/HRAnalytics";
import CertificationsModule from "./pages/CertificationsModule";
import AIAssistant from "./pages/AIAssistant";
import SaraHelp from "./pages/SaraHelp";
import Expenses from "./pages/Expenses";
import MyExpenses from "./pages/MyExpenses";
import RaiseConcern from "./pages/RaiseConcern";
import HrCases from "./pages/HrCases";
import HRDashboard from "./pages/HRDashboard";
import MedicalCheckups from "./pages/MedicalCheckups";
import EquipmentManagement from "./pages/EquipmentManagement";
import SsoCallback from "./pages/SsoCallback";
import MobileLogin from "./pages/mobile/MobileLogin";
import MobileLayout from "./pages/mobile/MobileLayout";
import MobileHome from "./pages/mobile/MobileHome";
import MobileCalendar from "./pages/mobile/MobileCalendar";
import MobileInbox from "./pages/mobile/MobileInbox";
import MobilePeople from "./pages/mobile/MobilePeople";
import MobileProfile from "./pages/mobile/MobileProfile";
import MobileSalary from "./pages/mobile/MobileSalary";
import MobileTimekeeping from "./pages/mobile/MobileTimekeeping";
import MobileSettings from "./pages/mobile/MobileSettings";
import MobileMedicalCheck from "./pages/mobile/MobileMedicalCheck";
import MobileRecruitment from "./pages/mobile/MobileRecruitment";
import MobileHRAnalytics from "./pages/mobile/MobileHRAnalytics";
import MobileTeamLeave from "./pages/mobile/MobileTeamLeave";
import MobileSurveysAdmin from "./pages/mobile/MobileSurveysAdmin";
import MobileOrganization from "./pages/mobile/MobileOrganization";


// Redirect HR/admin users to the dedicated HR dashboard, others stay on the default dashboard
function RoleBasedDashboard() {
  const { user } = useAuth();
  if (user?.role.mainRole === "hr" || user?.role.mainRole === "admin") {
    // HR/admin can opt into employee view by setting this flag
    let view = "hr";
    try { view = localStorage.getItem("hr_dashboard_view") ?? "hr"; } catch {}
    if (view !== "employee") {
      return <Navigate to="/hr-dashboard" replace />;
    }
  }
  return <Dashboard />;
}

// Guard: redirect to login if not authenticated
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { impersonation } = useSuperAdmin();
  // Allow access if there is either a real logged-in user OR an active
  // superadmin impersonation session (impersonation injects its own user
  // via localStorage, but guard against transient null during the reload).
  const hasActiveImpersonation =
    !!impersonation && new Date(impersonation.expiresAt) > new Date();
  if (!user && !hasActiveImpersonation) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Guard: HR/admin-only application modules
function RequireHrAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isHrAdmin = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";
  if (!isHrAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Guard: block routes whose module is disabled for the tenant. Mirrors the
// sidebar's gating (ALWAYS_ON_MODULES || enabledModules[key] !== false) so a
// disabled module can't be reached by typing its URL.
function RequireModule({ moduleKey, children }: { moduleKey: string; children: React.ReactNode }) {
  const { canAccess, ready, error, retry } = useModuleAccess();
  const { t } = useTranslation();
  if (error) return <div role="alert" className="p-6 space-y-3"><p>{t("common.configurationUnavailable")}</p><button className="underline" onClick={retry}>{t("common.retry")}</button></div>;
  if (!ready) return null;
  if (!canAccess(moduleKey)) return <ModuleDisabled />;
  return <>{children}</>;
}

// Redirect to dashboard if already logged in
function RedirectIfLoggedIn({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Mobile: redirect to /mobile if already logged in
function MobileRedirectIfLoggedIn({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/mobile" replace />;
  return <>{children}</>;
}

// Mobile: redirect to /mobile/login if not authenticated
function RequireMobileAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { impersonation } = useSuperAdmin();
  const hasActiveImpersonation =
    !!impersonation && new Date(impersonation.expiresAt) > new Date();
  if (!user && !hasActiveImpersonation) {
    return <Navigate to="/mobile/login" replace />;
  }
  return <>{children}</>;
}

// Mobile: redirect to /mobile if not hr/admin/c_level (admin module access)
function RequireMobileAdminRole({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isHrAdminOrCLevel =
    user?.role.mainRole === "hr" ||
    user?.role.mainRole === "admin" ||
    user?.role.subRole === "c_level";
  if (!isHrAdminOrCLevel) {
    return <Navigate to="/mobile" replace />;
  }
  return <>{children}</>;
}

// Guard: redirect to superadmin login if not authenticated as superadmin
function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useSuperAdmin();
  if (!isAuthenticated) {
    return <Navigate to="/superadmin/login" replace />;
  }
  return <>{children}</>;
}

// Persistent banner shown while a superadmin impersonation session is active
function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useSuperAdmin();
  const [timeLeft, setTimeLeft] = React.useState("");

  React.useEffect(() => {
    if (!impersonation) return;
    const update = () => {
      const diff = new Date(impersonation.expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        stopImpersonation();
        window.location.replace("/superadmin/accounts");
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, "0")}`);
    };
    update();
    const id = setInterval(update, 5000);
    return () => clearInterval(id);
  }, [impersonation, stopImpersonation]);

  if (!impersonation) return null;

  const roleLabel: Record<string, string> = {
    admin: "Admin", hr: "HR", user: "Employee",
    line_manager: "Line Manager", c_level: "C-Level",
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-between gap-3 px-4 py-2 bg-amber-500 text-amber-950 text-sm font-medium shadow-lg">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-bold whitespace-nowrap">IMPERSONATION MODE</span>
        <span className="text-amber-800">—</span>
        <span className="truncate">
          Viewing <strong>{impersonation.tenantName}</strong> as{" "}
          <strong>{roleLabel[impersonation.role] || impersonation.role}</strong>
          {impersonation.userName ? ` (${impersonation.userName})` : ""}
        </span>
        <span className="text-amber-700 whitespace-nowrap">· Read-only · Expires in {timeLeft}</span>
      </div>
      <button
        onClick={() => {
          stopImpersonation();
          // Full reload so AuthContext clears the injected tenant credentials
          window.location.replace("/superadmin/accounts");
        }}
        className="flex-shrink-0 bg-amber-950/20 hover:bg-amber-950/30 text-amber-950 font-semibold px-3 py-1 rounded text-xs transition-colors whitespace-nowrap"
      >
        Exit Impersonation
      </button>
    </div>
  );
}

// Wraps AppRoutes with the session-managed QueryClient and keys the route tree
// on the session identity so all pages fully remount on tenant/user change.
function SessionAwareRoutes() {
  const { sessionId, queryClient } = useTenantSession();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SuperAdminProvider>
          <ImpersonationBanner />
          <AppRoutes key={sessionId} />
        </SuperAdminProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

// Automatically redirects mobile-viewport users away from desktop routes
function MobileAutoRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isMobile === undefined) return; // wait for detection
    if (!isMobile) return; // desktop — do nothing

    const path = location.pathname;

    // Already on a mobile route — don't redirect
    if (path.startsWith("/mobile")) return;

    // Leave superadmin, public apply/schedule, SSO and password-reset pages alone
    if (
      path.startsWith("/superadmin") ||
      path.startsWith("/apply") ||
      path.startsWith("/schedule") ||
      path.startsWith("/preboarding") ||
      path === "/sso-callback" ||
      path === "/forgot-password" ||
      path === "/reset-password"
    ) return;

    if (path === "/login") {
      // Preserve query params (e.g. ?ssoError=...) so MobileLogin can display them
      navigate(`/mobile/login${location.search}`, { replace: true });
    } else {
      navigate("/mobile", { replace: true });
    }
  }, [isMobile, location.pathname, location.search, navigate]);

  return null;
}

function AppRoutes() {
  const { t } = useTranslation();
  return (
    <>
      <MobileAutoRedirect />
      <ProductTour />
      <Routes>
      {/* Super Admin Routes */}
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route
        path="/superadmin"
        element={
          <RequireSuperAdmin>
            <SuperAdminLayout />
          </RequireSuperAdmin>
        }
      >
        <Route index element={<Navigate to="accounts" replace />} />
        <Route path="accounts" element={<AccountsList />} />
        <Route path="accounts/:id/audit-log" element={<AccountAuditLog />} />
        <Route path="ai-usage" element={<AIUsage />} />
      </Route>

      {/* SSO callback — public, no auth guard */}
      <Route path="/sso-callback" element={<SsoCallback />} />

      {/* Login - redirects to dashboard if already logged in */}
      <Route
        path="/login"
        element={
          <RedirectIfLoggedIn>
            <Login />
          </RedirectIfLoggedIn>
        }
      />

      {/* Forgot / reset password — public, no auth */}
      <Route
        path="/forgot-password"
        element={
          <RedirectIfLoggedIn>
            <ForgotPassword />
          </RedirectIfLoggedIn>
        }
      />
      <Route
        path="/reset-password"
        element={
          <RedirectIfLoggedIn>
            <ResetPassword />
          </RedirectIfLoggedIn>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <RoleBasedDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/hr-dashboard"
        element={
          <RequireAuth>
            <RequireHrAdmin><HRDashboard /></RequireHrAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/employees"
        element={
          <RequireAuth>
            <RequireModule moduleKey="peopleDirectory">
              <Employees />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <RequireAuth>
            <EmployeeProfile />
          </RequireAuth>
        }
      />
      <Route
        path="/leave"
        element={
          <RequireAuth>
            <RequireModule moduleKey="leaveManagement">
              <LeaveManagement />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/timekeeping"
        element={
          <RequireAuth>
            <RequireModule moduleKey="timekeeping">
              <Timekeeping />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/timekeeping-admin"
        element={
          <RequireAuth>
            <RequireModule moduleKey="timekeeping">
              <TimekeepingAdmin />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/performance"
        element={
          <RequireAuth>
            <RequireModule moduleKey="careerDevelopment">
              <Performance />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/recruitment"
        element={
          <RequireAuth>
            <RequireModule moduleKey="recruitment">
              <Recruitment />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/career"
        element={
          <RequireAuth>
            <CareerDevelopment />
          </RequireAuth>
        }
      />
      <Route
        path="/payroll"
        element={
          <RequireAuth>
            <RequireModule moduleKey="payroll">
              <Payroll />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/equipment"
        element={
          <RequireAuth>
            <RequireModule moduleKey="equipmentManagement">
              <EquipmentManagement />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/my-salary"
        element={
          <RequireAuth>
            <RequireModule moduleKey="salary">
              <MySalary />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/compliance"
        element={
          <RequireAuth>
            <Compliance />
          </RequireAuth>
        }
      />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <RequireModule moduleKey="onboarding">
              <Onboarding />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/offboarding"
        element={<Navigate to="/onboarding?tab=offboarding" replace />}
      />
      <Route
        path="/my-onboarding"
        element={
          <RequireAuth>
            <RequireModule moduleKey="myOnboarding">
              <MyOnboarding />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/surveys"
        element={
          <RequireAuth>
            <RequireModule moduleKey="surveys">
              <Surveys />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/my-surveys"
        element={
          <RequireAuth>
            <RequireModule moduleKey="mySurveys">
              <MySurveys />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/raise-concern"
        element={
          <RequireAuth>
            <RequireModule moduleKey="raiseConcern">
              <RaiseConcern />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/hr-cases"
        element={
          <RequireAuth>
            <RequireModule moduleKey="hrOpenCases">
              <HrCases />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/workflows"
        element={
          <RequireAuth>
            <RequireModule moduleKey="workflows">
              <Workflows />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/my-tasks"
        element={
          <RequireAuth>
            <MyTasks />
          </RequireAuth>
        }
      />
      <Route
        path="/messages"
        element={
          <RequireAuth>
            <OnboardingMessages />
          </RequireAuth>
        }
      />
      <Route
        path="/organization"
        element={
          <RequireAuth>
            <RequireModule moduleKey="organization">
              <Organization />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/notifications"
        element={
          <RequireAuth>
            <Notifications />
          </RequireAuth>
        }
      />
      <Route
        path="/medical-checkups"
        element={
          <RequireAuth>
            <RequireHrAdmin>
              <RequireModule moduleKey="medicalCheckups">
                <MedicalCheckups />
              </RequireModule>
            </RequireHrAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <Settings />
          </RequireAuth>
        }
      />

      {/* Admin-specific routes */}
      <Route
        path="/user-management"
        element={<Navigate to="/organization?tab=employees" replace />}
      />
      <Route
        path="/audit-log"
        element={
          <RequireAuth>
            <RequireModule moduleKey="auditLog">
              <AuditLog />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/gdpr-retention"
        element={
          <RequireAuth>
            <GdprRetention />
          </RequireAuth>
        }
      />
      <Route
        path="/system-settings"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/field-permissions"
        element={
          <RequireAuth>
            <RequireModule moduleKey="fieldPermissions">
              <FieldPermissionsSettings />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/nav-permissions"
        element={
          <RequireAuth>
            <RequireModule moduleKey="navPermissions">
              <NavPermissionsSettings />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/teams-management"
        element={<Navigate to="/organization?tab=teams" replace />}
      />

      {/* User routes */}
      <Route
        path="/my-profile"
        element={
          <RequireAuth>
            <RequireModule moduleKey="myProfile">
              <MyProfile />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/documents"
        element={
          <RequireAuth>
            <Documents />
          </RequireAuth>
        }
      />
      <Route
        path="/my-documents"
        element={
          <RequireAuth>
            <MyDocuments />
          </RequireAuth>
        }
      />
      <Route
        path="/corporate-events"
        element={
          <RequireHrAdmin>
            <CorporateEventsAdmin />
          </RequireHrAdmin>
        }
      />
      <Route
        path="/corporate-calendar"
        element={
          <RequireAuth>
            <CorporateCalendar />
          </RequireAuth>
        }
      />
      <Route
        path="/corporate-calendar/:id"
        element={
          <RequireAuth>
            <CorporateCalendarEvent />
          </RequireAuth>
        }
      />

      {/* Line Manager routes */}
      <Route
        path="/team-overview"
        element={
          <RequireAuth>
            <RequireModule moduleKey="teamOverview">
              <TeamOverview />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/team-leave"
        element={
          <RequireAuth>
            <RequireModule moduleKey="teamLeave">
              <TeamLeave />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/team-performance"
        element={
          <RequireAuth>
            <TeamPerformance />
          </RequireAuth>
        }
      />
      <Route
        path="/approvals"
        element={
          <RequireAuth>
            <Approvals />
          </RequireAuth>
        }
      />

      {/* Career Coach routes */}
      <Route
        path="/coaching"
        element={
          <RequireAuth>
            <Coaching />
          </RequireAuth>
        }
      />
      <Route
        path="/career-plans"
        element={
          <RequireAuth>
            <CareerPlans />
          </RequireAuth>
        }
      />
      <Route
        path="/mentoring"
        element={
          <RequireAuth>
            <Mentoring />
          </RequireAuth>
        }
      />

      {/* C-Level routes */}
      <Route
        path="/analytics"
        element={
          <RequireAuth>
            <Analytics />
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth>
            <Reports />
          </RequireAuth>
        }
      />
      <Route
        path="/strategic-planning"
        element={
          <RequireAuth>
            <StrategicPlanning />
          </RequireAuth>
        }
      />

      <Route
        path="/hr-analytics"
        element={
          <RequireAuth>
            <RequireModule moduleKey="hrAnalytics">
              <HRAnalytics />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/certifications"
        element={
          <RequireAuth>
            <RequireModule moduleKey="certifications">
              <CertificationsModule />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <RequireAuth>
            <RequireModule moduleKey="aiAssistant">
              <AIAssistant />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/sara-help"
        element={
          <RequireAuth>
            <SaraHelp />
          </RequireAuth>
        }
      />

      <Route
        path="/expenses"
        element={
          <RequireAuth>
            <RequireModule moduleKey="expenses">
              <Expenses />
            </RequireModule>
          </RequireAuth>
        }
      />
      <Route
        path="/my-expenses"
        element={
          <RequireAuth>
            <RequireModule moduleKey="myExpenses">
              <MyExpenses />
            </RequireModule>
          </RequireAuth>
        }
      />

      {/* Public apply page — no auth required */}
      <Route path="/apply/:slug" element={<Apply />} />

      {/* Public interview scheduling page — no auth required */}
      <Route path="/schedule/:tenantSlug/:token" element={<InterviewSchedule />} />

      {/* Public pre-boarding form — no auth required */}
      <Route path="/preboarding/:tenantSlug/:token" element={<PreBoarding />} />

      {/* ── Mobile PWA Routes ────────────────────────────────────────────── */}
      <Route
        path="/mobile/login"
        element={
          <MobileRedirectIfLoggedIn>
            <MobileLogin />
          </MobileRedirectIfLoggedIn>
        }
      />
      <Route
        path="/mobile"
        element={
          <RequireMobileAuth>
            <MobileHome />
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/calendar"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="leaveManagement">
              <MobileLayout title={t("mobile.nav.calendar")}>
                <MobileCalendar />
              </MobileLayout>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/inbox"
        element={
          <RequireMobileAuth>
            <MobileLayout title={t("mobile.inbox.notifications")}>
              <MobileInbox />
            </MobileLayout>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/people"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="peopleDirectory">
              <MobileLayout title={t("nav.peopleDirectory")}>
                <MobilePeople />
              </MobileLayout>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/profile"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="myProfile">
              <MobileProfile />
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/salary"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="salary">
              <MobileSalary />
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/settings"
        element={
          <RequireMobileAuth>
            <MobileSettings />
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/timekeeping"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="timekeeping">
              <MobileTimekeeping />
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/medical-check"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="medicalCheckups">
              <MobileMedicalCheck />
            </RequireModule>
          </RequireMobileAuth>
        }
      />

      {/* ── Mobile Admin Views (HR/Admin/C-Level) ───────────────────────── */}
      <Route
        path="/mobile/recruitment"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="recruitment">
              <RequireMobileAdminRole>
                <MobileLayout title={t("nav.recruitment")} showBack>
                  <MobileRecruitment />
                </MobileLayout>
              </RequireMobileAdminRole>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/hr-analytics"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="hrAnalytics">
              <RequireMobileAdminRole>
                <MobileLayout title={t("nav.hrAnalytics")} showBack>
                  <MobileHRAnalytics />
                </MobileLayout>
              </RequireMobileAdminRole>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/team-leave"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="teamLeave">
              <RequireMobileAdminRole>
                <MobileLayout title={t("nav.teamLeave")} showBack>
                  <MobileTeamLeave />
                </MobileLayout>
              </RequireMobileAdminRole>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/surveys-admin"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="surveys">
              <RequireMobileAdminRole>
                <MobileLayout title={t("nav.surveys")} showBack>
                  <MobileSurveysAdmin />
                </MobileLayout>
              </RequireMobileAdminRole>
            </RequireModule>
          </RequireMobileAuth>
        }
      />
      <Route
        path="/mobile/organization"
        element={
          <RequireMobileAuth>
            <RequireModule moduleKey="organization">
              <RequireMobileAdminRole>
                <MobileLayout title={t("nav.organization")} showBack>
                  <MobileOrganization />
                </MobileLayout>
              </RequireMobileAdminRole>
            </RequireModule>
          </RequireMobileAuth>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" storageKey="besthr-theme">
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <TenantSessionProvider>
            <SessionAwareRoutes />
          </TenantSessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </ThemeProvider>
);

export default App;
